import React, { ReactNode, useEffect } from 'react';
import {
    Dialog as MaterialDialog,
    DialogContent,
    Box,
    Typography,
    useMediaQuery,
    IconButton,
    Backdrop,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SxProps } from '@mui/system';

interface DialogProps {
    isOpen: boolean;
    title: string;
    children: ReactNode;
    onClose: () => void;
    fullScreenOnMobile?: boolean;
    contentSX?: SxProps;
    onExited?: () => void;
    description?: string;
}

const Dialog = ({
    title,
    children,
    onClose,
    isOpen,
    fullScreenOnMobile = false,
    contentSX = {},
    onExited,
    description,
}: DialogProps) => {
    const isMobile = useMediaQuery('(max-width: 500px)');

    useEffect(() => {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const notification = document.getElementById('notification-container');

        if (isOpen && notification && onExited && scrollbarWidth > 0) {
            notification.style.paddingRight = `${scrollbarWidth}px`;
        }
    }, [isOpen, onExited]);

    const handleExit = () => {
        const notification = document.getElementById('notification-container');

        if (notification) {
            notification.style.paddingRight = '';
        }

        onExited && onExited();
    };

    const handleDialogClose = (event: object, reason: string) => {
        if (reason !== 'backdropClick') {
            onClose();
        }
    };

    return (
        <MaterialDialog
            open={isOpen}
            onClose={handleDialogClose}
            maxWidth={false}
            fullWidth={false}
            fullScreen={fullScreenOnMobile && isMobile}
            data-testid='dialog'
            disableEscapeKeyDown={true}
            slots={{
                backdrop: Backdrop,
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    },
                },
            }}
            sx={{
                ...((isMobile && {
                    '& .MuiDialog-container': {
                        '& > .MuiPaper-root': {
                            padding: '8px 0 16px',
                            width: '100%',
                            maxWidth: '100%',
                            margin: 0,
                            maxHeight: '100%',
                            borderRadius: '0',
                            height: '100vh',
                        },
                    },
                }) ||
                    {}),
                '.MuiPaper-root': {
                    borderRadius: '6px',
                    margin: '0',
                    width: 'auto',
                    maxWidth: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
            TransitionProps={{
                onExited: handleExit,
            }}
        >
            <Box sx={{ padding: '24px 24px 8px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '16px',
                        width: '100%',
                    }}
                >
                    <Typography
                        variant='h4'
                        sx={{
                            color: 'common.black',
                            fontSize: '24px',
                        }}
                    >
                        {title}
                    </Typography>
                    <IconButton sx={{ p: 0 }} data-testid='close-dialog' onClick={onClose}>
                        <CloseIcon sx={{ color: 'common.black' }} />
                    </IconButton>
                </Box>
                {description && (
                    <Typography variant='subtitle1' sx={{ color: 'common.grey1', paddingBottom: '8px' }}>
                        {description}
                    </Typography>
                )}
            </Box>
            <DialogContent
                id='dialog-content'
                sx={{
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    },
                    ...contentSX,
                }}
            >
                {children}
            </DialogContent>
        </MaterialDialog>
    );
};

export default Dialog;
